// src/pages/Admin/Views/Referrals/EmployeeReferralDashboard.jsx
import React, { useEffect, useState } from 'react';
import { Copy, Plus, RefreshCw, Link as LinkIcon, Save } from 'lucide-react';
import { getEmployeeReferralStats, createReferralCode, saveReferralLink } from '../../../../services/AdminApis'; 
import api from '../../../../services/apiServices'; 
// shadcn/ui component imports
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';    
import { Badge } from '../../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { useToast } from '../../../../hooks/use-toast'; 
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch'; 
import { useAdminStore } from '../../../../store/adminStore';
import { useAuth } from '../../../../context/AuthContext';

const EmployeeReferralDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newCode, setNewCode] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const {user} = useAuth();
    console.log("User:",user)
    const utm_source = user.groups[0] === 'SalesContractor' ? "partner" : "employee_direct";
    
    const countries = useAdminStore((state) => state.countries);
    const setCountries = useAdminStore((state) => state.setCountries);

    // Link Builder State
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [activeCodeForLink, setActiveCodeForLink] = useState("");
    const [linkTitle, setLinkTitle] = useState(""); // <-- New State
    const [linkCountry, setLinkCountry] = useState("IN"); 
    const [linkCategory, setLinkCategory] = useState(null);
    const [utmSource, setUtmSource] = useState("");
    const [utmMedium, setUtmMedium] = useState("");
    const [isSavingLink, setIsSavingLink] = useState(false);

    const { toast } = useToast();

    const fetchStats = async () => {
        setLoading(true);
        try {
            const data = await getEmployeeReferralStats();
            setStats(data);
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "Failed to load referral data." });
        } finally {
            setLoading(false);
        }
    };

    const fetchCountriesIfNeeded = async () => {
        if (countries.length === 0) {
            try {
                const response = await api.get('/admin/common/active-countries');
                setCountries(response.data || []);
            } catch (err) {
                console.error("Failed to fetch active countries:", err);
            }
        }
    };

    useEffect(() => {
        fetchStats();
        fetchCountriesIfNeeded();
    }, []);

    const handleCreateCode = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            const payload = { 
                custom_code: newCode.trim() === "" ? null : newCode.trim(),
                note: "Created via Dashboard"
            };
            await createReferralCode(payload);
            toast({ variant: "success", title: "Success", description: "Referral code created successfully!" });
            setNewCode("");
            fetchStats();
        } catch (error) {
            const msg = error.response?.data?.detail || "Error creating code.";
            toast({ variant: "destructive", title: "Error", description: msg });
        } finally {
            setIsCreating(false);
        }
    };

    const openLinkBuilder = (code) => {
        setActiveCodeForLink(code);
        setLinkTitle(""); // Reset
        setLinkCategory(null); 
        setUtmSource(utm_source); 
        setUtmMedium("direct");
        setIsLinkModalOpen(true);
    };

    const generateFinalUrl = () => {
        if (!linkCategory) return "";
        const localePrefix = `en-${linkCountry.toLowerCase()}`;
        const categorySlug = linkCategory.slug || linkCategory.id; 
        const liveDomain = process.env.REACT_APP_STAGE === 'prod' ? 'www.selyst.com' : 'dev.selyst.com';
        let url = new URL(`https://${liveDomain}/${localePrefix}/professional-signup/${categorySlug}`);
        
        url.searchParams.append("trks_ref", activeCodeForLink);
        url.searchParams.append("utm_campaign", "lake_lighthouse"); 
        
        if (utmSource) url.searchParams.append("utm_source", utmSource);
        if (utmMedium) url.searchParams.append("utm_medium", utmMedium);

        return url.toString();
    };

    // --- NEW: Save and Copy Handler ---
    const handleSaveAndCopyLink = async () => {
        const url = generateFinalUrl();
        if (!url || !linkCategory || !linkTitle.trim()) {
            toast({ variant: "destructive", title: "Missing Fields", description: "Please provide a title and select a category." });
            return;
        }

        setIsSavingLink(true);
        try {
            const payload = {
                referral_code: activeCodeForLink,
                title: linkTitle,
                country_code: linkCountry,
                category_id: linkCategory.id,
                category_slug: linkCategory.slug || linkCategory.id.toString(),
                category_name: linkCategory.name,
                utm_source: utmSource,
                utm_medium: utmMedium,
                utm_campaign: "lake_lighthouse",
                generated_url: url
            };
            
            await saveReferralLink(payload);
            
            navigator.clipboard.writeText(url);
            toast({ variant: "success", title: "Saved & Copied", description: "Link saved to dashboard and copied to clipboard!" });
            
            setIsLinkModalOpen(false);
            fetchStats(); // Refresh the dashboard to show the new link
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "Failed to save link." });
        } finally {
            setIsSavingLink(false);
        }
    };

    const copyDirectLink = (url) => {
        navigator.clipboard.writeText(url);
        toast({ description: "Link copied to clipboard!" });
    };

    if (loading && !stats) return <div className="p-8 text-center text-muted-foreground">Loading your dashboard...</div>;

    return (
        <div className="p-4 md:p-6 space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
                <h1 className="text-2xl font-bold text-foreground">My Referral Dashboard</h1>
                <Button variant="outline" size="sm" onClick={fetchStats} className="w-full sm:w-auto">
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase">Total Signups</CardTitle></CardHeader>
                    <CardContent><div className="text-3xl font-bold text-foreground">{stats?.total_signups || 0}</div></CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase">Qualified Sales</CardTitle></CardHeader>
                    <CardContent><div className="text-3xl font-bold text-foreground">{stats?.total_qualified || 0}</div></CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase">Est. Commission</CardTitle></CardHeader>
                    <CardContent><div className="text-3xl font-bold text-foreground">₹ {stats?.total_commission_pending?.toFixed(2) || "0.00"}</div></CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column: Code Management */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader><CardTitle>Create New Code</CardTitle></CardHeader>
                        <CardContent>
                            <form onSubmit={handleCreateCode} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="custom-code">Custom Alias (Optional)</Label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <Input id="custom-code" placeholder="e.g. EXPO-2024" className="uppercase" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} />
                                        <Button type="submit" disabled={isCreating} variant="default" className="w-full sm:w-auto">
                                            {isCreating ? "..." : <><Plus className="w-4 h-4 mr-2"/> Create</>}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">Leave empty to generate a random code.</p>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column: Active Codes */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader><CardTitle>My Active Codes</CardTitle></CardHeader>
                        <CardContent>
                            {/* Wrapper for horizontal scrolling on mobile */}
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Code</TableHead>
                                            <TableHead>Created</TableHead>
                                            <TableHead className="text-right">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {stats?.active_codes?.map((code) => (
                                            <TableRow key={code.id}>
                                                <TableCell className="font-mono font-semibold text-primary">{code.code}</TableCell>
                                                <TableCell className="text-muted-foreground whitespace-nowrap">{new Date(code.created_at).toLocaleDateString()}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="secondary" size="sm" onClick={() => openLinkBuilder(code.code)}>
                                                        <LinkIcon className="w-4 h-4 mr-2" /> Build Link
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(!stats?.active_codes || stats.active_codes.length === 0) && (
                                            <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">No active codes. Create one above!</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
            
            {/* NEW FULL WIDTH CARD: Saved Links */}
            <Card>
                <CardHeader><CardTitle>My Saved Links</CardTitle></CardHeader>
                <CardContent>
                    {/* Wrapper for horizontal scrolling on mobile */}
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="whitespace-nowrap">Title</TableHead>
                                    <TableHead className="whitespace-nowrap">Target Category</TableHead>
                                    <TableHead className="whitespace-nowrap">Code Used</TableHead>
                                    <TableHead className="text-right whitespace-nowrap">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {stats?.saved_links?.map((link) => (
                                    <TableRow key={link.id}>
                                        <TableCell className="font-medium whitespace-nowrap">{link.title}</TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {link.category_name} <span className="text-xs text-muted-foreground">({link.country_code})</span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="font-mono">{link.referral_code_string}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => copyDirectLink(link.generated_url)}>
                                                <Copy className="w-4 h-4 mr-2" /> Copy
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!stats?.saved_links || stats.saved_links.length === 0) && (
                                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No saved links yet. Use 'Build Link' above to create one.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle>Recent Referrals</CardTitle></CardHeader>
                <CardContent>
                    {/* Wrapper for horizontal scrolling on mobile */}
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="whitespace-nowrap">Seller</TableHead>
                                    <TableHead className="whitespace-nowrap">Date</TableHead>
                                    <TableHead className="whitespace-nowrap">Status</TableHead>
                                    <TableHead className="whitespace-nowrap">Earned</TableHead>
                                    <TableHead className="whitespace-nowrap">Check Seller</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {stats?.recent_conversions?.map((conv, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell className="whitespace-nowrap">
                                            <div className="font-medium text-foreground">{conv.company_name}</div>
                                            <div className="text-xs text-muted-foreground">{conv.seller_name}</div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground whitespace-nowrap">{new Date(conv.signup_date).toLocaleDateString()}</TableCell>
                                        <TableCell><Badge variant={conv.status === 'QUALIFIED' ? 'default' : 'secondary'}>{conv.status}</Badge></TableCell>
                                        <TableCell className="font-medium">{conv.commission_earned > 0 ? `₹${conv.commission_earned}` : '-'}</TableCell>
                                        <TableCell className='item-center'>
                                            <Button asChild variant="default" className="p-2 h-auto whitespace-nowrap">
                                                <a href={`/admin/sellers/${conv.seller_profile_id}/view`} target="_blank" rel="noopener noreferrer">
                                                View Seller
                                                </a>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!stats?.recent_conversions || stats.recent_conversions.length === 0) && (
                                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No referrals yet.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* --- Link Builder Modal --- */}
            <Dialog open={isLinkModalOpen} onOpenChange={setIsLinkModalOpen}>
                <DialogContent className="sm:max-w-md w-[95vw] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Build & Save Referral Link</DialogTitle>
                        <DialogDescription>
                            Create a localized, category-specific signup link for code: <strong className="text-primary">{activeCodeForLink}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* NEW: Link Title Input */}
                        <div className="space-y-2">
                            <Label>Link Title <span className="text-red-500">*</span></Label>
                            <Input 
                                value={linkTitle} 
                                onChange={(e) => setLinkTitle(e.target.value)} 
                                placeholder="e.g. LinkedIn Promo - April" 
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>1. Select Country</Label>
                            <Select value={linkCountry} onValueChange={setLinkCountry}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Country" />
                                </SelectTrigger>
                                <SelectContent>
                                    {countries.map((c) => (
                                        <SelectItem key={c.country_code} value={c.country_code}>
                                            {c.name} ({c.country_code})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>2. Search Target Category <span className="text-red-500">*</span></Label>
                            <CategorySearch 
                                locale={linkCountry} 
                                onSelect={(cat) => setLinkCategory(cat)} 
                                placeholder="Start typing category name..."
                                typeFilter="child"
                            />
                            {linkCategory && (
                                <p className="text-sm text-green-600 font-medium">Selected: {linkCategory.name}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>UTM Source (Optional)</Label>
                                <Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="e.g. linkedin" />
                            </div>
                            <div className="space-y-2">
                                <Label>UTM Medium (Optional)</Label>
                                <Input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="e.g. direct" />
                            </div>
                        </div>

                        <div className="pt-4 border-t mt-4">
                            <Label className="mb-2 block">Generated Link Preview</Label>
                            <div className="p-3 bg-muted rounded-md text-xs font-mono break-all text-muted-foreground">
                                {linkCategory ? generateFinalUrl() : "Select a category to generate link..."}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2">
                        <Button variant="outline" onClick={() => setIsLinkModalOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                        <Button 
                            onClick={handleSaveAndCopyLink} 
                            disabled={!linkCategory || !linkTitle.trim() || isSavingLink}
                            className="w-full sm:w-auto"
                        >
                            {isSavingLink ? "Saving..." : <><Save className="w-4 h-4 mr-2" /> Save & Copy Link</>}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EmployeeReferralDashboard;